import * as z from 'zod';

export const GetLoyaltyProgramRequest = z.object({});
export const GetLoyaltyProgramResponse = z.object({
  loyaltyProgram: z
    .object({
      name: z
        .string()
        .describe('Program name.')
        .min(2)
        .max(50)
        .optional()
        .nullable(),
      pointDefinition: z
        .object({
          customName: z
            .string()
            .describe(
              'Display name for the program\'s collectible unit.\n\nIt\'s recommended to use a plural, for example `"Stars"`.\n\nIn contrast to a custom name, the default `"Points"` name is translated and adjusted to singular based on circumstances.\n\nDefault: `"Points"`.'
            )
            .max(20)
            .optional()
            .nullable(),
          icon: z
            .string()
            .describe('Details about the points icon.')
            .optional(),
        })
        .describe("Details about the program's collectible unit.")
        .optional(),
      status: z
        .enum(['UNKNOWN', 'DRAFT', 'ACTIVE', 'PAUSED'])
        .describe(
          'Program status.\nCustomers can only earn or redeem points while the program is `"ACTIVE"`.\n\nDefault: `"DRAFT"`'
        )
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the program was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the program was updated.')
        .optional()
        .nullable(),
      pointsExpiration: z
        .object({
          status: z
            .enum(['DISABLED', 'ENABLED'])
            .describe('Status of the points expiration feature.')
            .optional(),
          monthsOfInactivity: z
            .number()
            .int()
            .describe(
              'How many months a member should be inactive to lose the collected points.'
            )
            .min(1)
            .max(12)
            .optional(),
          expiringPointsPercentage: z
            .number()
            .int()
            .describe(
              'Percentage of points that a member loses after being inactive.'
            )
            .min(1)
            .max(100)
            .optional(),
        })
        .describe('Configuration for the points expiration feature.')
        .optional(),
      premiumFeatures: z
        .object({
          loyaltyProgram: z
            .boolean()
            .describe('Whether there are any loyalty program premium features.')
            .optional(),
          tiers: z
            .boolean()
            .describe('Whether this loyalty program includes tiers.')
            .optional(),
          pointsExpiration: z
            .boolean()
            .describe('Whether this loyalty program includes point expiration.')
            .optional(),
        })
        .describe('Information about the available program premium features.')
        .optional(),
    })
    .describe('Retrieved loyalty program.')
    .optional(),
});
export const BulkGetLoyaltyProgramRequest = z.object({});
export const BulkGetLoyaltyProgramResponse = z.object({
  programInSites: z
    .array(
      z.object({
        metaSiteId: z
          .string()
          .describe('Metasite ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        loyaltyProgram: z
          .object({
            name: z
              .string()
              .describe('Program name.')
              .min(2)
              .max(50)
              .optional()
              .nullable(),
            pointDefinition: z
              .object({
                customName: z
                  .string()
                  .describe(
                    'Display name for the program\'s collectible unit.\n\nIt\'s recommended to use a plural, for example `"Stars"`.\n\nIn contrast to a custom name, the default `"Points"` name is translated and adjusted to singular based on circumstances.\n\nDefault: `"Points"`.'
                  )
                  .max(20)
                  .optional()
                  .nullable(),
                icon: z
                  .string()
                  .describe('Details about the points icon.')
                  .optional(),
              })
              .describe("Details about the program's collectible unit.")
              .optional(),
            status: z
              .enum(['UNKNOWN', 'DRAFT', 'ACTIVE', 'PAUSED'])
              .describe(
                'Program status.\nCustomers can only earn or redeem points while the program is `"ACTIVE"`.\n\nDefault: `"DRAFT"`'
              )
              .optional(),
            _createdDate: z
              .date()
              .describe('Date and time the program was created.')
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe('Date and time the program was updated.')
              .optional()
              .nullable(),
            pointsExpiration: z
              .object({
                status: z
                  .enum(['DISABLED', 'ENABLED'])
                  .describe('Status of the points expiration feature.')
                  .optional(),
                monthsOfInactivity: z
                  .number()
                  .int()
                  .describe(
                    'How many months a member should be inactive to lose the collected points.'
                  )
                  .min(1)
                  .max(12)
                  .optional(),
                expiringPointsPercentage: z
                  .number()
                  .int()
                  .describe(
                    'Percentage of points that a member loses after being inactive.'
                  )
                  .min(1)
                  .max(100)
                  .optional(),
              })
              .describe('Configuration for the points expiration feature.')
              .optional(),
            premiumFeatures: z
              .object({
                loyaltyProgram: z
                  .boolean()
                  .describe(
                    'Whether there are any loyalty program premium features.'
                  )
                  .optional(),
                tiers: z
                  .boolean()
                  .describe('Whether this loyalty program includes tiers.')
                  .optional(),
                pointsExpiration: z
                  .boolean()
                  .describe(
                    'Whether this loyalty program includes point expiration.'
                  )
                  .optional(),
              })
              .describe(
                'Information about the available program premium features.'
              )
              .optional(),
          })
          .describe('Loyalty program.')
          .optional(),
      })
    )
    .optional(),
});
export const UpdateLoyaltyProgramRequest = z.object({
  loyaltyProgram: z
    .object({
      name: z
        .string()
        .describe('Program name.')
        .min(2)
        .max(50)
        .optional()
        .nullable(),
      pointDefinition: z
        .object({
          customName: z
            .string()
            .describe(
              'Display name for the program\'s collectible unit.\n\nIt\'s recommended to use a plural, for example `"Stars"`.\n\nIn contrast to a custom name, the default `"Points"` name is translated and adjusted to singular based on circumstances.\n\nDefault: `"Points"`.'
            )
            .max(20)
            .optional()
            .nullable(),
          icon: z
            .string()
            .describe('Details about the points icon.')
            .optional(),
        })
        .describe("Details about the program's collectible unit.")
        .optional(),
      status: z.enum(['UNKNOWN', 'DRAFT', 'ACTIVE', 'PAUSED']).optional(),
      _createdDate: z
        .date()
        .describe('Date and time the program was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the program was updated.')
        .optional()
        .nullable(),
      pointsExpiration: z
        .object({
          status: z.enum(['DISABLED', 'ENABLED']).optional(),
          monthsOfInactivity: z
            .number()
            .int()
            .describe(
              'How many months a member should be inactive to lose the collected points.'
            )
            .min(1)
            .max(12)
            .optional(),
          expiringPointsPercentage: z
            .number()
            .int()
            .describe(
              'Percentage of points that a member loses after being inactive.'
            )
            .min(1)
            .max(100)
            .optional(),
        })
        .describe('Configuration for the points expiration feature.')
        .optional(),
      premiumFeatures: z
        .object({
          loyaltyProgram: z
            .boolean()
            .describe('Whether there are any loyalty program premium features.')
            .optional(),
          tiers: z
            .boolean()
            .describe('Whether this loyalty program includes tiers.')
            .optional(),
          pointsExpiration: z
            .boolean()
            .describe('Whether this loyalty program includes point expiration.')
            .optional(),
        })
        .describe('Information about the available program premium features.')
        .optional(),
    })
    .describe('Loyalty program fields to update.'),
});
export const UpdateLoyaltyProgramResponse = z.object({
  loyaltyProgram: z
    .object({
      name: z
        .string()
        .describe('Program name.')
        .min(2)
        .max(50)
        .optional()
        .nullable(),
      pointDefinition: z
        .object({
          customName: z
            .string()
            .describe(
              'Display name for the program\'s collectible unit.\n\nIt\'s recommended to use a plural, for example `"Stars"`.\n\nIn contrast to a custom name, the default `"Points"` name is translated and adjusted to singular based on circumstances.\n\nDefault: `"Points"`.'
            )
            .max(20)
            .optional()
            .nullable(),
          icon: z
            .string()
            .describe('Details about the points icon.')
            .optional(),
        })
        .describe("Details about the program's collectible unit.")
        .optional(),
      status: z
        .enum(['UNKNOWN', 'DRAFT', 'ACTIVE', 'PAUSED'])
        .describe(
          'Program status.\nCustomers can only earn or redeem points while the program is `"ACTIVE"`.\n\nDefault: `"DRAFT"`'
        )
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the program was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the program was updated.')
        .optional()
        .nullable(),
      pointsExpiration: z
        .object({
          status: z
            .enum(['DISABLED', 'ENABLED'])
            .describe('Status of the points expiration feature.')
            .optional(),
          monthsOfInactivity: z
            .number()
            .int()
            .describe(
              'How many months a member should be inactive to lose the collected points.'
            )
            .min(1)
            .max(12)
            .optional(),
          expiringPointsPercentage: z
            .number()
            .int()
            .describe(
              'Percentage of points that a member loses after being inactive.'
            )
            .min(1)
            .max(100)
            .optional(),
        })
        .describe('Configuration for the points expiration feature.')
        .optional(),
      premiumFeatures: z
        .object({
          loyaltyProgram: z
            .boolean()
            .describe('Whether there are any loyalty program premium features.')
            .optional(),
          tiers: z
            .boolean()
            .describe('Whether this loyalty program includes tiers.')
            .optional(),
          pointsExpiration: z
            .boolean()
            .describe('Whether this loyalty program includes point expiration.')
            .optional(),
        })
        .describe('Information about the available program premium features.')
        .optional(),
    })
    .describe('Updated loyalty program.')
    .optional(),
});
export const ActivateLoyaltyProgramRequest = z.object({});
export const ActivateLoyaltyProgramResponse = z.object({
  loyaltyProgram: z
    .object({
      name: z
        .string()
        .describe('Program name.')
        .min(2)
        .max(50)
        .optional()
        .nullable(),
      pointDefinition: z
        .object({
          customName: z
            .string()
            .describe(
              'Display name for the program\'s collectible unit.\n\nIt\'s recommended to use a plural, for example `"Stars"`.\n\nIn contrast to a custom name, the default `"Points"` name is translated and adjusted to singular based on circumstances.\n\nDefault: `"Points"`.'
            )
            .max(20)
            .optional()
            .nullable(),
          icon: z
            .string()
            .describe('Details about the points icon.')
            .optional(),
        })
        .describe("Details about the program's collectible unit.")
        .optional(),
      status: z
        .enum(['UNKNOWN', 'DRAFT', 'ACTIVE', 'PAUSED'])
        .describe(
          'Program status.\nCustomers can only earn or redeem points while the program is `"ACTIVE"`.\n\nDefault: `"DRAFT"`'
        )
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the program was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the program was updated.')
        .optional()
        .nullable(),
      pointsExpiration: z
        .object({
          status: z
            .enum(['DISABLED', 'ENABLED'])
            .describe('Status of the points expiration feature.')
            .optional(),
          monthsOfInactivity: z
            .number()
            .int()
            .describe(
              'How many months a member should be inactive to lose the collected points.'
            )
            .min(1)
            .max(12)
            .optional(),
          expiringPointsPercentage: z
            .number()
            .int()
            .describe(
              'Percentage of points that a member loses after being inactive.'
            )
            .min(1)
            .max(100)
            .optional(),
        })
        .describe('Configuration for the points expiration feature.')
        .optional(),
      premiumFeatures: z
        .object({
          loyaltyProgram: z
            .boolean()
            .describe('Whether there are any loyalty program premium features.')
            .optional(),
          tiers: z
            .boolean()
            .describe('Whether this loyalty program includes tiers.')
            .optional(),
          pointsExpiration: z
            .boolean()
            .describe('Whether this loyalty program includes point expiration.')
            .optional(),
        })
        .describe('Information about the available program premium features.')
        .optional(),
    })
    .describe('Activated loyalty program.')
    .optional(),
});
export const PauseLoyaltyProgramRequest = z.object({});
export const PauseLoyaltyProgramResponse = z.object({
  loyaltyProgram: z
    .object({
      name: z
        .string()
        .describe('Program name.')
        .min(2)
        .max(50)
        .optional()
        .nullable(),
      pointDefinition: z
        .object({
          customName: z
            .string()
            .describe(
              'Display name for the program\'s collectible unit.\n\nIt\'s recommended to use a plural, for example `"Stars"`.\n\nIn contrast to a custom name, the default `"Points"` name is translated and adjusted to singular based on circumstances.\n\nDefault: `"Points"`.'
            )
            .max(20)
            .optional()
            .nullable(),
          icon: z
            .string()
            .describe('Details about the points icon.')
            .optional(),
        })
        .describe("Details about the program's collectible unit.")
        .optional(),
      status: z
        .enum(['UNKNOWN', 'DRAFT', 'ACTIVE', 'PAUSED'])
        .describe(
          'Program status.\nCustomers can only earn or redeem points while the program is `"ACTIVE"`.\n\nDefault: `"DRAFT"`'
        )
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the program was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the program was updated.')
        .optional()
        .nullable(),
      pointsExpiration: z
        .object({
          status: z
            .enum(['DISABLED', 'ENABLED'])
            .describe('Status of the points expiration feature.')
            .optional(),
          monthsOfInactivity: z
            .number()
            .int()
            .describe(
              'How many months a member should be inactive to lose the collected points.'
            )
            .min(1)
            .max(12)
            .optional(),
          expiringPointsPercentage: z
            .number()
            .int()
            .describe(
              'Percentage of points that a member loses after being inactive.'
            )
            .min(1)
            .max(100)
            .optional(),
        })
        .describe('Configuration for the points expiration feature.')
        .optional(),
      premiumFeatures: z
        .object({
          loyaltyProgram: z
            .boolean()
            .describe('Whether there are any loyalty program premium features.')
            .optional(),
          tiers: z
            .boolean()
            .describe('Whether this loyalty program includes tiers.')
            .optional(),
          pointsExpiration: z
            .boolean()
            .describe('Whether this loyalty program includes point expiration.')
            .optional(),
        })
        .describe('Information about the available program premium features.')
        .optional(),
    })
    .describe('Paused loyalty program.')
    .optional(),
});
export const GetLoyaltyProgramPremiumFeaturesRequest = z.object({});
export const GetLoyaltyProgramPremiumFeaturesResponse = z.object({
  loyaltyProgram: z
    .boolean()
    .describe('Whether there are any loyalty program premium features.')
    .optional(),
  tiers: z
    .boolean()
    .describe('Whether this loyalty program includes tiers.')
    .optional(),
  pointsExpiration: z
    .boolean()
    .describe('Whether this loyalty program includes point expiration.')
    .optional(),
});
export const EnablePointsExpirationRequest = z.object({});
export const EnablePointsExpirationResponse = z.object({
  loyaltyProgram: z
    .object({
      name: z
        .string()
        .describe('Program name.')
        .min(2)
        .max(50)
        .optional()
        .nullable(),
      pointDefinition: z
        .object({
          customName: z
            .string()
            .describe(
              'Display name for the program\'s collectible unit.\n\nIt\'s recommended to use a plural, for example `"Stars"`.\n\nIn contrast to a custom name, the default `"Points"` name is translated and adjusted to singular based on circumstances.\n\nDefault: `"Points"`.'
            )
            .max(20)
            .optional()
            .nullable(),
          icon: z
            .string()
            .describe('Details about the points icon.')
            .optional(),
        })
        .describe("Details about the program's collectible unit.")
        .optional(),
      status: z
        .enum(['UNKNOWN', 'DRAFT', 'ACTIVE', 'PAUSED'])
        .describe(
          'Program status.\nCustomers can only earn or redeem points while the program is `"ACTIVE"`.\n\nDefault: `"DRAFT"`'
        )
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the program was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the program was updated.')
        .optional()
        .nullable(),
      pointsExpiration: z
        .object({
          status: z
            .enum(['DISABLED', 'ENABLED'])
            .describe('Status of the points expiration feature.')
            .optional(),
          monthsOfInactivity: z
            .number()
            .int()
            .describe(
              'How many months a member should be inactive to lose the collected points.'
            )
            .min(1)
            .max(12)
            .optional(),
          expiringPointsPercentage: z
            .number()
            .int()
            .describe(
              'Percentage of points that a member loses after being inactive.'
            )
            .min(1)
            .max(100)
            .optional(),
        })
        .describe('Configuration for the points expiration feature.')
        .optional(),
      premiumFeatures: z
        .object({
          loyaltyProgram: z
            .boolean()
            .describe('Whether there are any loyalty program premium features.')
            .optional(),
          tiers: z
            .boolean()
            .describe('Whether this loyalty program includes tiers.')
            .optional(),
          pointsExpiration: z
            .boolean()
            .describe('Whether this loyalty program includes point expiration.')
            .optional(),
        })
        .describe('Information about the available program premium features.')
        .optional(),
    })
    .describe('Loyalty program with enabled points expiration feature.')
    .optional(),
});
export const DisablePointsExpirationRequest = z.object({});
export const DisablePointsExpirationResponse = z.object({
  loyaltyProgram: z
    .object({
      name: z
        .string()
        .describe('Program name.')
        .min(2)
        .max(50)
        .optional()
        .nullable(),
      pointDefinition: z
        .object({
          customName: z
            .string()
            .describe(
              'Display name for the program\'s collectible unit.\n\nIt\'s recommended to use a plural, for example `"Stars"`.\n\nIn contrast to a custom name, the default `"Points"` name is translated and adjusted to singular based on circumstances.\n\nDefault: `"Points"`.'
            )
            .max(20)
            .optional()
            .nullable(),
          icon: z
            .string()
            .describe('Details about the points icon.')
            .optional(),
        })
        .describe("Details about the program's collectible unit.")
        .optional(),
      status: z
        .enum(['UNKNOWN', 'DRAFT', 'ACTIVE', 'PAUSED'])
        .describe(
          'Program status.\nCustomers can only earn or redeem points while the program is `"ACTIVE"`.\n\nDefault: `"DRAFT"`'
        )
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the program was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the program was updated.')
        .optional()
        .nullable(),
      pointsExpiration: z
        .object({
          status: z
            .enum(['DISABLED', 'ENABLED'])
            .describe('Status of the points expiration feature.')
            .optional(),
          monthsOfInactivity: z
            .number()
            .int()
            .describe(
              'How many months a member should be inactive to lose the collected points.'
            )
            .min(1)
            .max(12)
            .optional(),
          expiringPointsPercentage: z
            .number()
            .int()
            .describe(
              'Percentage of points that a member loses after being inactive.'
            )
            .min(1)
            .max(100)
            .optional(),
        })
        .describe('Configuration for the points expiration feature.')
        .optional(),
      premiumFeatures: z
        .object({
          loyaltyProgram: z
            .boolean()
            .describe('Whether there are any loyalty program premium features.')
            .optional(),
          tiers: z
            .boolean()
            .describe('Whether this loyalty program includes tiers.')
            .optional(),
          pointsExpiration: z
            .boolean()
            .describe('Whether this loyalty program includes point expiration.')
            .optional(),
        })
        .describe('Information about the available program premium features.')
        .optional(),
    })
    .describe('Loyalty program with disabled points expiration feature.')
    .optional(),
});
